import { html } from 'htm/preact';
import { render } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { FLAVIO, LULA, parseResults, Snapshot } from './data';

// Mesmo desenho do banner da 2.ª volta das presidenciais de fevereiro
// (banner-presidenciais-2026-round2-results.ts); muda a fonte dos dados.

interface CandidateInfo {
    number: number;
    name: string;
    photo: string;
    color: string;
    textColor: string;
}

// Lados fixos, como Seguro/Ventura em fevereiro: Lula à esquerda, Flávio à direita.
// Vermelho (PT) e amarelo (bolsonarismo); as mesmas cores estão no SCSS, no sublinhado da 1.ª volta.
const CANDIDATES: Record<'left' | 'right', CandidateInfo> = {
    left: {
        number: LULA,
        name: 'Lula',
        // Recortada (quadrado centrado na cara, sem o topo do chapéu) para ter o mesmo
        // enquadramento da do Flávio; a original do TSE é lula.jpeg.
        photo: 'https://s3.eu-west-1.amazonaws.com/s3.observador.pt/wp-content/uploads/imgs-br-2026/lula-v2.jpeg',
        color: '#E5484D',
        textColor: '#fff',
    },
    right: {
        number: FLAVIO,
        name: 'Flávio Bolsonaro',
        photo: 'https://s3.eu-west-1.amazonaws.com/s3.observador.pt/wp-content/uploads/imgs-br-2026/flavio-bolsonaro.jpeg',
        color: '#FFB224',
        // Branco sobre amarelo não se lê.
        textColor: '#1a1a1a',
    },
};

const DEFAULT_DATA_URL = 'https://election-data.observador.pt/live/BR26-2026-10-25/PR/br/results.json';

// Fecho das urnas: 17h00 em Brasília (UTC-3) = 20h00 em Lisboa, já na hora de inverno.
const POLLS_CLOSE = Date.parse('2026-10-25T20:00:00Z');

const REFRESH_MS = 60 * 1000;

const banner = document.querySelector<HTMLDivElement>('#widget-banner-brasil-2026-round2-results');
const container = banner?.querySelector<HTMLDivElement>('.candidatos-container');
const dataUrl = banner?.dataset.source || DEFAULT_DATA_URL;
const linkUrl = banner?.dataset.url || '';
const winner = Number(banner?.dataset.winner) || 0;

const pctFormat = new Intl.NumberFormat('pt-PT', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const sectionsFormat = new Intl.NumberFormat('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const votesFormat = new Intl.NumberFormat('pt-PT');
// "25/10, 23:25", em hora de Lisboa.
const timeFormat = new Intl.DateTimeFormat('pt-PT', { timeZone: 'Europe/Lisbon', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

function formatCountdown(distance: number): string {
    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0 || days > 0) parts.push(`${hours}h`);
    if (minutes > 0 || hours > 0 || days > 0) parts.push(`${minutes}m`);
    parts.push(`${seconds}s`);

    return parts.join(' ');
}

function BrasilRound2Results() {
    // Últimos dados bons: uma falha de rede ou um ficheiro inválido não os apaga.
    const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
    const [countdown, setCountdown] = useState<string>('');

    useEffect(() => {
        let cancelled = false;

        async function fetchData() {
            try {
                const response = await fetch(dataUrl, { cache: 'no-cache' });
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                const parsed = parseResults(await response.json());
                if (!parsed) {
                    throw new Error('invalid results.json');
                }
                if (!cancelled) {
                    setSnapshot(parsed);
                }
            } catch (error) {
                console.error('Brasil 2026: failed to fetch election data', error);
            }
        }

        fetchData();
        const interval = setInterval(fetchData, REFRESH_MS);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        const update = () => {
            const distance = POLLS_CLOSE - Date.now();
            setCountdown(distance > 0 ? formatCountdown(distance) : '');
        };

        update();
        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
    }, []);

    const hasData = !!snapshot && snapshot.sectionsCountedPct > 0;

    const pctOf = (c: CandidateInfo) => (hasData ? snapshot!.candidates[c.number].pct : 0);
    const votesOf = (c: CandidateInfo) => (hasData ? snapshot!.candidates[c.number].votes : 0);

    const leftPct = pctOf(CANDIDATES.left);
    const rightPct = pctOf(CANDIDATES.right);
    const total = leftPct + rightPct;
    const leftNormalized = total > 0 ? (leftPct / total) * 100 : 50;
    const rightNormalized = total > 0 ? (rightPct / total) * 100 : 50;

    // Vencedor forçado no back office: o outro fica a cinzento, com ou sem 100%.
    const isLoser = (c: CandidateInfo) => winner !== 0 && winner !== c.number;

    // "Última atualização: 25/10, 23:25  |  Apurados: 12,40% das secções"
    let estado = html`<strong>A aguardar resultados:</strong> ${countdown || 'urnas fechadas'}`;
    if (hasData) {
        const apurados = `Apurados: ${sectionsFormat.format(snapshot!.sectionsCountedPct)}% das secções`;
        estado = snapshot!.generatedAt ? html`<strong>Última atualização: ${timeFormat.format(snapshot!.generatedAt)}</strong><span class="separador">|</span>${apurados}` : html`${apurados}`;
    }

    const candidatos = html`<div class="candidatos-container-inner ${!hasData ? 'waiting-state' : ''}">${renderCandidate(CANDIDATES.left, 'left', leftNormalized)} ${renderCandidate(CANDIDATES.right, 'right', rightNormalized)}</div>`;

    // Só os candidatos são clicáveis; a linha de estado e as de baixo (com o link do TSE) ficam fora.
    return html`
        ${linkUrl ? html`<a href=${linkUrl} target="_blank" rel="noopener" class="candidatos-wrapper">${candidatos}</a>` : html`<div class="candidatos-wrapper">${candidatos}</div>`}
        <p class="info-linha estado">${estado}</p>
    `;

    // Sempre a cores, mesmo a 0% antes da contagem: só o vencedor forçado
    // (classe .loser no contentor) põe o outro candidato a cinzento.
    function renderCandidate(candidate: CandidateInfo, position: 'left' | 'right', normalizedPercentage: number) {
        // Largura proporcional, como em fevereiro (50% = metade inteira). A barra da
        // direita cresce para o centro: com mais de 50%, a margem negativa leva-a por
        // cima da metade esquerda, e as duas barras encontram-se no ponto da proporção.
        const width = normalizedPercentage * 2;
        const offset = position === 'right' ? ` margin-left: ${100 - width}%;` : '';

        const percentageContainer = html`
            <div class="percentage-container">
                <p class="candidate-name">${candidate.name}</p>
                <div class="percentagem" style="width: ${width}%;${offset} background-color: ${candidate.color}; color: ${candidate.textColor};">${pctFormat.format(pctOf(candidate))}%</div>
                <span class="votos">${votesFormat.format(votesOf(candidate))} votos</span>
            </div>
        `;

        const picture = html`
            <picture>
                <img src="${candidate.photo}" alt="${candidate.name}" width="65" height="65" />
            </picture>
        `;

        return html` <div class="candidate-container ${position}-candidate ${isLoser(candidate) ? 'loser' : ''}" style="--cor-candidato: ${candidate.color};">${position === 'left' ? html`${picture}${percentageContainer}` : html`${percentageContainer}${picture}`}</div> `;
    }
}

if (container) {
    render(html`<${BrasilRound2Results} />`, container);
}
